/**
 * Sending domains and their DNS records.
 *
 * The screen exists to get a domain's records published correctly, so it
 * reports each one independently. "Verification failed" on its own sends
 * someone hunting through a DNS panel; "the DKIM record is not found yet, the
 * MAIL FROM domain is not set" tells them what to do.
 *
 * The DKIM and return-path records are the provider's. It signs the domain's
 * mail with a key of its own, so the screen shows what the provider asked for
 * and what it said about each record, and checks only DMARC and tracking in DNS
 * itself.
 */

import { useState, type FormEvent } from "react";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { Providers } from "./Providers";
import { CopyValue } from "../components/CopyValue";
import { Icon, type IconName } from "../components/Sprites";
import { useSession } from "../session";
import type { DNSRecord, Domain, DomainDetail, DomainProviderCheck } from "../api/types";

export function Domains() {
  const { orgId, can } = useSession();
  const [selected, setSelected] = useState<string | null>(null);

  const list = useAsync<Domain[]>((signal) => api.get<Domain[]>("/v1/domains", { signal }), [orgId]);

  const domains = list.data ?? [];
  const activeId = selected ?? domains[0]?.id ?? null;

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Domains</h1>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={list.reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="content">
        {list.error && <p className="field-error">{list.error.message}</p>}

        {/* Providers first: a verified domain with nothing to send through
            delivers nothing, and on a fresh install this is the panel that is
            empty. */}
        <Providers />

        {can("domains:manage") && <AddDomain onAdded={(domain) => {
          list.reload();
          setSelected(domain.id);
        }} />}

        {domains.length === 0 && !list.loading && (
          <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
            <div className="oc-empty">
              <div className="oc-empty__title">No sending domains yet</div>
              <div className="oc-empty__hint">
                Mail sent from an unverified domain lands in spam folders or is rejected outright.
                Add the domain at your email provider first, then add it here: the records to
                publish come from the provider.
              </div>
            </div>
          </div>
        )}

        {domains.length > 0 && (
          <div className="row domains-layout" style={{ alignItems: "flex-start", gap: "var(--oc-space-3)" }}>
            <DomainList domains={domains} activeId={activeId} onSelect={setSelected} />
            {activeId && <DomainDetailPanel domainId={activeId} onChanged={list.reload} />}
          </div>
        )}
      </div>
    </>
  );
}

function DomainList({
  domains,
  activeId,
  onSelect,
}: {
  domains: Domain[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="oc-panel" style={{ width: 280, flex: "none" }}>
      <div className="oc-panel__header">
        <span className="oc-label">{domains.length} domains</span>
      </div>
      <div style={{ padding: 4 }}>
        {domains.map((domain) => (
          <button
            key={domain.id}
            type="button"
            className="oc-menu__option org__option"
            aria-selected={domain.id === activeId}
            onClick={() => onSelect(domain.id)}
          >
            <span className="oc-truncate oc-mono">{domain.domain}</span>
            <VerifiedMark verified={domain.verified} />
          </button>
        ))}
      </div>
    </div>
  );
}

function VerifiedMark({ verified }: { verified: boolean }) {
  return verified ? (
    <span className="oc-text-success row" style={{ gap: 4 }}>
      <Icon name="check" size={12} />
    </span>
  ) : (
    <span className="oc-text-warning row" style={{ gap: 4 }}>
      <Icon name="warning" size={12} />
    </span>
  );
}

function DomainDetailPanel({ domainId, onChanged }: { domainId: string; onChanged: () => void }) {
  const { can } = useSession();
  const { data, error, loading, reload } = useAsync<DomainDetail>(
    (signal) => api.get<DomainDetail>(`/v1/domains/${encodeURIComponent(domainId)}`, { signal }),
    [domainId],
  );

  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  const verify = async () => {
    setChecking(true);
    setCheckError(null);
    try {
      await api.post<DomainDetail>(`/v1/domains/${encodeURIComponent(domainId)}/verify`);
      reload();
      onChanged();
    } catch (err) {
      setCheckError(err instanceof ApiError ? err.message : "The check could not run.");
    } finally {
      setChecking(false);
    }
  };

  const remove = async () => {
    // Not unaffected when OpenComms tracks this domain's campaigns: every link
    // in them goes through its tracking host, and that host stops being served
    // with the domain.
    const warning =
      data?.tracking_verified && data.tracking_host
        ? `Remove ${data.domain}? The links in campaigns already sent from it go through ${data.tracking_host}, and stop working once the domain is gone.`
        : `Remove ${data?.domain}? Mail already sent is unaffected.`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/v1/domains/${encodeURIComponent(domainId)}`);
      onChanged();
    } catch (err) {
      setCheckError(err instanceof ApiError ? err.message : "Could not remove the domain.");
    }
  };

  if (error) return <p className="field-error grow">{error.message}</p>;
  if (!data) return <div className="oc-panel grow" style={{ padding: "var(--oc-gutter)" }}>{loading ? "Loading…" : null}</div>;

  return (
    <div className="oc-panel grow">
      <div className="oc-panel__header">
        <div style={{ minWidth: 0 }}>
          <div className="oc-label" style={{ marginBottom: 4 }}>
            Sending domain
          </div>
          <div className="oc-headline-md oc-truncate">{data.domain}</div>
        </div>
        <div className="row">
          {data.last_checked_at && (
            <span className="oc-mono-sm oc-text-muted">
              checked {new Date(data.last_checked_at).toLocaleString()}
            </span>
          )}
          {/* The check writes what it finds, so it needs domains:manage, and
              offering it to everybody else was a button that always 403s. */}
          {can("domains:manage") && (
            <>
              <button type="button" className="oc-btn oc-btn--sm" disabled={checking} onClick={() => void verify()}>
                {checking ? "Checking…" : "Check"}
              </button>
              <button type="button" className="oc-btn oc-btn--danger oc-btn--sm" onClick={() => void remove()}>
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      <div className="oc-panel__body stack">
        {checkError && <p className="field-error">{checkError}</p>}

        <ProviderAnswer check={data.provider_check} checkError={data.check_error} />

        {data.tracking_host && (
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            {data.tracking_verified ? (
              <>
                Campaign opens and clicks from this domain are tracked by OpenComms, on{" "}
                <span className="oc-mono-sm">{data.tracking_host}</span>. The provider&apos;s
                tracking is switched off for those messages.
              </>
            ) : (
              <>
                Campaign opens and clicks from this domain are tracked by your provider. Publish the
                tracking record below to track them here, on your own subdomain.
              </>
            )}
          </p>
        )}

        {!data.verified && (
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            Publish these records with your DNS provider, then run the check. Propagation is usually
            minutes but can take up to a day, and the provider may take longer still to look — a
            record that is correct but not yet found reads the same as a missing one from here.
          </p>
        )}

        {data.records.map((record) => (
          <RecordRow key={`${record.purpose}-${record.name}`} record={record} />
        ))}
      </div>
    </div>
  );
}

/** What the provider said, in words, and whether that answer is still current. */
function ProviderAnswer({ check, checkError }: { check?: DomainProviderCheck; checkError?: string }) {
  if (!check) {
    return (
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        This domain has not been checked with its provider yet. Run the check to see the records the
        provider needs.
      </p>
    );
  }

  const who = check.provider_name ?? "The provider";
  return (
    <div className="stack" style={{ gap: "var(--oc-space-2)" }}>
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        {STATUS_TEXT[check.status](who)} Checked {new Date(check.checked_at).toLocaleString()}.
      </p>
      {check.stale && (
        <p className="oc-body-sm oc-text-warning" style={{ margin: 0 }}>
          {who} is no longer the default provider, so these may not be the records mail is signed
          with now. Run the check again.
        </p>
      )}
      {checkError && (
        <p className="field-error">
          The latest check could not ask the provider: {checkError}. The records below are its last
          answer.
        </p>
      )}
      {check.notes.length > 0 && (
        <ul className="oc-body-sm" style={{ margin: 0, paddingLeft: "1.25em" }}>
          {check.notes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const STATUS_TEXT: Record<DomainProviderCheck["status"], (who: string) => string> = {
  found: (who) => `${who} gave these records for the domain, with what it has found.`,
  not_found: (who) => `${who} does not know this domain yet.`,
  unavailable: (who) => `${who} cannot report this domain's records.`,
  no_provider: () => "There is no default email provider to ask.",
};

const PURPOSE_LABEL: Record<string, string> = {
  return_path: "return path",
};

const PURPOSE_NOTE: Record<string, string> = {
  dkim: "The provider signs your mail with this key, so receivers can prove it came from you.",
  return_path: "The envelope domain the provider sends from, so SPF aligns with your From domain.",
  dmarc: "Tells receivers what to do when a check fails, and where to report.",
  tracking: "Serves this domain's campaign links from your own subdomain.",
};

function RecordRow({ record }: { record: DNSRecord }) {
  const state = recordState(record);

  return (
    <div className="oc-panel" style={{ background: "var(--oc-bg-inset)" }}>
      <div className="oc-panel__header">
        <div className="row" style={{ gap: "var(--oc-space-2)" }}>
          <span className={`row ${state.className}`} style={{ gap: 6 }}>
            <Icon name={state.icon} size={12} />
            <span className="oc-label" style={{ color: "inherit" }}>
              {PURPOSE_LABEL[record.purpose] ?? record.purpose}
            </span>
          </span>
          <span className="oc-mono-sm oc-text-muted">{record.type}</span>
          {!record.required && <span className="oc-mono-sm oc-text-muted">optional</span>}
          {record.suggested && <span className="oc-mono-sm oc-text-muted">suggested</span>}
        </div>
        <span className="oc-body-sm oc-text-secondary">{state.label}</span>
      </div>

      <div className="oc-panel__body stack">
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          {record.note ?? PURPOSE_NOTE[record.purpose]}
        </p>

        <div className="stack" style={{ gap: "var(--oc-space-2)" }}>
          <div>
            <div className="oc-label" style={{ marginBottom: 4 }}>
              Name
            </div>
            <div className="oc-dns-value">
              <CopyValue value={record.name} className="oc-truncate" />
            </div>
          </div>

          <div>
            <div className="oc-label" style={{ marginBottom: 4 }}>
              Value
            </div>
            <div className="oc-dns-value">
              <CopyValue value={record.value} className="oc-truncate" />
            </div>
          </div>

          {record.priority !== undefined && (
            <div>
              <div className="oc-label" style={{ marginBottom: 4 }}>
                Priority
              </div>
              <div className="oc-dns-value">
                <CopyValue value={String(record.priority)} className="oc-truncate" />
              </div>
            </div>
          )}

          {/* A 2048-bit DKIM key is 392 characters and exceeds the 255-character
              limit for one TXT string. Most DNS providers split it for you;
              some want the pieces typed separately, so both are offered. */}
          {record.chunks && record.chunks.length > 1 && (
            <details>
              <summary>
                <Icon name="chevron" className="caret oc-text-secondary" />
                <span className="oc-label">Split into {record.chunks.length} DNS strings</span>
              </summary>
              <div className="stack" style={{ marginTop: "var(--oc-space-2)", gap: 4 }}>
                {record.chunks.map((chunk, index) => (
                  <div className="oc-dns-value" key={index}>
                    <CopyValue value={chunk} className="oc-truncate" />
                  </div>
                ))}
              </div>
            </details>
          )}

          {record.error && <p className="field-error">{record.error}</p>}

          {record.found && record.found !== record.value && (
            <div>
              <div className="oc-label" style={{ marginBottom: 4 }}>
                Currently published
              </div>
              <pre className="oc-code" style={{ whiteSpace: "pre-wrap" }}>{record.found}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One shape per outcome — not shades of the same tick. A record nobody has a
 * verdict on is not "not found": the provider gave none for it, or the record
 * waits on a setting the provider does not have yet.
 */
function recordState(record: DNSRecord): { icon: IconName; className: string; label: string } {
  if (record.suggested) return { icon: "settings", className: "oc-text-secondary", label: "Set up at the provider first" };
  if (record.verified) return { icon: "check", className: "oc-text-success", label: "Verified" };
  if (record.found) return { icon: "close", className: "oc-text-danger", label: "Does not match" };
  if (record.verified === undefined) return { icon: "dns", className: "oc-text-secondary", label: "Not checked" };
  return { icon: "warning", className: "oc-text-warning", label: "Not found" };
}

function AddDomain({ onAdded }: { onAdded: (domain: Domain) => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const created = await api.post<DomainDetail>("/v1/domains", { domain: name.trim() });
      setName("");
      onAdded(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the domain.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="oc-panel" onSubmit={(event) => void submit(event)}>
      <div className="oc-panel__body row" style={{ gap: "var(--oc-space-2)" }}>
        <div className="grow">
          <input
            className="oc-input"
            placeholder="mail.example.com"
            aria-label="Domain to add"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <button type="submit" className="oc-btn oc-btn--primary" disabled={busy || !name.trim()}>
          <Icon name="plus" size={12} />
          {busy ? "Adding…" : "Add domain"}
        </button>
      </div>
      {error && (
        <div className="oc-panel__body" style={{ paddingTop: 0 }}>
          <p className="field-error">
            {error}
          </p>
        </div>
      )}
    </form>
  );
}
